import React, { ReactNode, useEffect, useRef } from "react";
import styled from "styled-components";
import { Transition } from "react-transition-group";
import { colors, sizes } from "@sonder/ui/source/styled/theme";

export const DRAWER_TRANSITION_SPEED = 100;

interface StyledDrawerProps {
  drawerState: string;
}

const StyledDrawer = styled.div<StyledDrawerProps>`
  background-color: ${colors.white};
  box-shadow: ${colors.shadow};
  box-sizing: border-box;
  max-width: 100%;
  height: 100vh;
  overflow: auto;
  pointer-events: all;
  transform: translateX(
    ${({ drawerState }) => (drawerState === "entered" ? "0%" : "-100%")}
  );
  transition: transform ${DRAWER_TRANSITION_SPEED}ms ease-in-out;

  @media screen and (min-width: ${sizes.minBreakpoint}) {
    max-width: 32rem;
  }
`;

export interface DrawerFunctionParams extends StyledDrawerProps {
  closeDrawer?(): void;
}

export type DrawerRenderFunction = (props: DrawerFunctionParams) => ReactNode;

interface DrawerProps {
  children: DrawerRenderFunction;
  className?: string;
  isOpen: boolean;
  onClose?(): void;
}

export const Drawer = ({
  children,
  className,
  isOpen = false,
  onClose,
}: DrawerProps) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const closeDrawer = () => {
    if (onClose) onClose();
  };
  const onOutsideClick = (e: MouseEvent) => {
    if (
      drawerRef.current &&
      !drawerRef.current.contains(e.target as HTMLElement)
    )
      closeDrawer();
  };
  useEffect(() => {
    if (isOpen) document.addEventListener("click", onOutsideClick);

    return () => {
      document.removeEventListener("click", onOutsideClick);
    };
  }, [isOpen]);
  return (
    <Transition
      mountOnEnter
      unmountOnExit
      in={isOpen}
      timeout={DRAWER_TRANSITION_SPEED}
    >
      {drawerState => (
        <StyledDrawer
          className={className}
          drawerState={drawerState}
          ref={drawerRef}
        >
          {children({ drawerState, closeDrawer })}
        </StyledDrawer>
      )}
    </Transition>
  );
};
